import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'

import type { AxiosError } from 'axios'
import type { Socket } from 'socket.io-client'

import { ChatbotApiCaller } from '@frontend/api'
import type {
	AnswerError,
	Chatbot
} from '@types'
import { ChatInput, Message } from '@ui/chat'
import ChatContent from '@ui/chat/ChatContent'
import { useChatContext } from '@ui/chat/ChatProvider'
import { LoadingSpinner } from '@ui/components'
import { useSentry } from '@utils/frontend'

import { initSocket } from '../../utils/socket'
import { getAccessToken } from '../../utils/storage'

export default function ChatbotAppChatMessages({ chat, isInPreviewMode }: { readonly chat: Chatbot; readonly isInPreviewMode: boolean }) {
	const [inputValue, setInputValue] = useState('')
	const [socket, setSocket] = useState<Socket>()
	const [isLoaded, setIsLoaded] = useState(false)
	const { chatDispatch, chatState } = useChatContext()

	const { captureException } = useSentry()

	const accessToken = getAccessToken(chat.id, isInPreviewMode)

	useEffect(() => {
		if (accessToken) {
			const socket = initSocket(accessToken)

			socket.connect()
			setSocket(socket)
		}
	}, [accessToken])

	// Load default messages
	useEffect(() => {
		async function loadMessages() {
			try {
				const messages = await ChatbotApiCaller.getMessages(chat.id, accessToken!)

				if (messages.length) {
					messages.unshift({
						id: '0',
						role: 'assistant',
						content: chat.initialMessage,
						isReasoning: false,
						isFollowUp: false,
						createdAt: new Date()
					})
				} else {
					messages.push({
						id: '0',
						role: 'assistant',
						content: chat.initialMessage,
						isReasoning: false,
						isFollowUp: false,
						suggestedPrompts: chat.initialQuestions,
						createdAt: new Date()
					})
				}

				chatDispatch({ type: 'setMessages', messages })
				setIsLoaded(true)
			} catch (error) {
				captureException(error)
				toast.error('Error while fetching this conversation')
			}
		}

		loadMessages()
	}, [])

	// ---------
	// HANDLERS
	// ---------
	async function handleSubmit(inputValue: string) {
		await sendMessage(inputValue)
	}

	async function handleFollowUp(message: string) {
		await sendMessage(message, true)
	}

	/**
	 * Method that send the query (user or followup) and return the answer
	 * by streaming the response
	 * @param query Query to send
	 */
	async function sendMessage(query: string, isFollowUp = false) {
		const scrollButton = document.querySelector('#chat-scrollbutton') as HTMLButtonElement

		if (scrollButton) {
			scrollButton.click()
		}

		const accessToken = getAccessToken(chat.id, isInPreviewMode)

		if (!accessToken) {
			return
		}

		try {
			const { assistantMessage, userMessage } = await ChatbotApiCaller.sendMessage(chat.id, query, accessToken, isFollowUp)

			chatDispatch({ type: 'addMessages', messages: [userMessage, assistantMessage] })
		} catch (err) {
			captureException(err)
			const error = (err as AxiosError<AnswerError>).response?.data

			handleSendError(error)
		}
	}

	function handleSendError(error?: AnswerError | undefined) {
		const toastMessage = error?.message
			?? 'We are really sorry, our servers are overloaded and we could not process your request. Please try again.'

		toast.error(<b>{ toastMessage }</b>)

		if (chatState.messages.length) {
			chatDispatch({ type: 'updateMessage', message: { id: chatState.messages[chatState.messages.length - 1]?.id, status: 'failed' } })
		}
	}

	if (!isLoaded) {
		return (<div className="w-full h-full flex justify-center items-center"><LoadingSpinner size="small" /></div>)
	}

	return (
		<>
			<ChatContent>
				{ socket
					? chatState.messages.map((message, index) => (
						<Message
							key={ index }
							chatId={ chat.id }
							socket={ socket }
							message={ message }
							isLastMessage={ index === chatState.messages.length - 1 }
							onFollowUp={ handleFollowUp }
						/>
					))
					: null }
			</ChatContent>
			<div className="mb-4">
				<ChatInput
					isDisabled={ chatState.isThinkingOrAnswering || inputValue.trim() === '' }
					inputValue={ inputValue }
					setInputValue={ setInputValue }
					onSubmit={ handleSubmit }
				/>
			</div>
		</>
	)
}
